"use client"

import React, { useEffect, useState } from "react"
import { CreditCard, MessageCircle, Smartphone } from "lucide-react"

export interface DownloadAppData {
  page: { title: string; tagline: string; subtitle: string }
  appStoreLabel: string
  appStoreCaption: string
  playStoreLabel: string
  playStoreCaption: string
  qrSectionTitle: string
  qrSectionSubtitle: string
  openAppCta: string
  featuresTitle: string
  features: { title: string; desc: string }[]
}

interface Props {
  data: DownloadAppData
  lng: string
  appStoreUrl: string
  playStoreUrl: string
}

type Platform = "ios" | "android" | "desktop"

const FEATURE_ICONS = [Smartphone, CreditCard, MessageCircle]

function detectPlatform(): Platform {
  if (typeof window === "undefined") return "desktop"
  const ua = navigator.userAgent.toLowerCase()
  if (/iphone|ipad|ipod/.test(ua)) return "ios"
  if (/android/.test(ua)) return "android"
  return "desktop"
}

export default function DownloadAppClient({
  data,
  lng,
  appStoreUrl,
  playStoreUrl,
}: Props) {
  const [platform, setPlatform] = useState<Platform>("desktop")

  useEffect(() => {
    setPlatform(detectPlatform())
  }, [])

  // QR code points back to this page; phone-side UA detection redirects to the
  // correct store. Lets desktop users print a QR and have visitors land here.
  const qrUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/${lng}/downloadapp`
      : `https://yohomobile.com/${lng}/downloadapp`
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=8&data=${encodeURIComponent(
    qrUrl
  )}`

  const showIOS = platform === "ios" || platform === "desktop"
  const showAndroid = platform === "android" || platform === "desktop"
  const showQr = platform === "desktop"

  return (
    <div className="min-h-screen bg-white">
      <section>
        <div className="mx-auto max-w-3xl px-5 pt-10 md:px-10 md:pt-16">
          <div className="flex flex-col items-center text-center">
            <h1 className="mb-3 text-3xl font-extrabold tracking-tight text-stone-900 md:text-4xl">
              {data.page.tagline}
            </h1>
            <p className="mb-10 max-w-md text-base text-stone-500 md:text-lg">
              {data.page.subtitle}
            </p>

            {/* Store buttons */}
            <div
              className={`flex w-full flex-col gap-3 sm:flex-row sm:gap-4 ${
                showIOS && showAndroid ? "max-w-md" : "max-w-xs"
              }`}
            >
              {showIOS && (
                <a
                  href={appStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-track="click_app_store_download"
                  className="group flex flex-1 items-center justify-center gap-3 rounded-2xl bg-stone-900 px-5 py-3.5 text-white transition-transform hover:scale-[1.02]"
                >
                  <svg
                    className="h-7 w-7"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden
                  >
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                  </svg>
                  <div className="text-left leading-tight">
                    <div className="text-[10px] uppercase tracking-wide opacity-80">
                      {data.appStoreCaption}
                    </div>
                    <div className="text-base font-semibold">App Store</div>
                  </div>
                </a>
              )}
              {showAndroid && (
                <a
                  href={playStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-track="click_play_store_download"
                  className="group flex flex-1 items-center justify-center gap-3 rounded-2xl bg-stone-900 px-5 py-3.5 text-white transition-transform hover:scale-[1.02]"
                >
                  <svg
                    className="h-7 w-7"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden
                  >
                    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.808 1.626L15.119 12l2.579-2.491zM5.864 2.658L16.802 8.99l-2.302 2.303-8.636-8.635z" />
                  </svg>
                  <div className="text-left leading-tight">
                    <div className="text-[10px] uppercase tracking-wide opacity-80">
                      {data.playStoreCaption}
                    </div>
                    <div className="text-base font-semibold">Google Play</div>
                  </div>
                </a>
              )}
            </div>
          </div>

          {/* QR (desktop only) */}
          {showQr && (
            <div className="mt-12 flex flex-col items-center">
              <div className="mb-4 flex items-center gap-3 text-sm text-stone-400">
                <span className="h-px w-12 bg-stone-200" aria-hidden />
                <span>{data.qrSectionTitle}</span>
                <span className="h-px w-12 bg-stone-200" aria-hidden />
              </div>
              <div className="rounded-2xl border border-stone-200 bg-white p-3">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={qrSrc}
                  alt={data.qrSectionTitle}
                  width={200}
                  height={200}
                  className="block"
                />
              </div>
              <p className="mt-3 max-w-xs text-center text-xs text-stone-500">
                {data.qrSectionSubtitle}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Features */}
      <section>
        <div className="mx-auto max-w-3xl px-5 pb-16 pt-12 md:px-10 md:pb-24 md:pt-20">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {data.features.map((f, i) => {
              const Icon = FEATURE_ICONS[i] ?? Smartphone
              return (
                <div
                  key={f.title}
                  className="rounded-2xl border border-stone-200 bg-white p-5 text-center md:p-6"
                >
                  <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-home-orange_1/10">
                    <Icon className="h-5 w-5 text-home-orange_1" aria-hidden />
                  </div>
                  <p className="text-sm font-bold text-stone-900 md:text-base">
                    {f.title}
                  </p>
                  <p className="mt-1 text-xs text-stone-500 md:text-[13px]">
                    {f.desc}
                  </p>
                </div>
              )
            })}
          </div>
        </div>
      </section>
    </div>
  )
}
